import { AlertTriangle, Check, Languages, Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import {
  fetchAvSync,
  fetchSyncReport,
  regenerateSubtitles,
  shiftCaptions,
  type AvSyncReport,
  type SyncReport,
} from "../../api";
import { useIsMobile } from "../../lib/useIsMobile";
import { ctaGhostStyle } from "../styles";

/**
 * Audio/video sync guard verdict. Distinct from SyncCard (captions↔audio):
 * this verifies the on-screen PICTURE matches the speaker. Renders nothing when
 * the guard didn't run for this clip (no av-sync.json) so an "off" pipeline is
 * visually unchanged.
 *
 * Both cards live in the Details dialog's Sync tab (spec 2026-09-16 §2.2).
 */
export function AvSyncCard({
  jobId,
  blocked,
}: {
  jobId: string;
  /** True when this job produced no video (failed/incomplete) — a drift verdict
   *  then means the guard BLOCKED the clip, so we frame it protectively. */
  blocked: boolean;
}): React.JSX.Element | null {
  const [report, setReport] = useState<AvSyncReport | null | undefined>(undefined);
  const [showAll, setShowAll] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReport(undefined);
    setShowAll(false);
    setLoadError(false);
    fetchAvSync(jobId)
      .then((r) => { if (!cancelled) setReport(r); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (loadError) {
    return <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 12 }}>Couldn't load the picture check.</div>;
  }
  // Loading, or guard never ran → show nothing (keeps "off" pipelines clean).
  if (report === undefined || report === null) return null;

  const drift = report.desync;
  const color = drift ? "#F87171" : "#4ADE80";
  const bg = drift ? "rgba(248,113,113,.10)" : "rgba(74,222,128,.10)";
  // Honest copy: green is "checked, none found" — NOT a guarantee. The check
  // can miss drift when both speakers share the frame (see verifyAvSync's
  // scope note), so we never tell the user the picture is definitely fine.
  const badge = drift ? "Picture drift" : "Picture checked";
  const label = drift
    ? "The person on screen doesn't match who's talking"
    : "No mismatch found between the picture and the speaker";
  const mismatched = report.pairs.filter((p) => !p.match);
  const shown = showAll ? report.pairs : mismatched;

  return (
    <div
      style={{
        background: "rgba(16,18,27,.72)",
        border: `1px solid ${color}33`,
        borderRadius: 18,
        padding: 18,
        marginBottom: 20,
        boxShadow: drift ? "0 0 30px rgba(248,113,113,.10)" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            color,
            background: bg,
            border: `1px solid ${color}33`,
            textTransform: "uppercase",
            letterSpacing: ".08em",
          }}
        >
          {drift ? <AlertTriangle size={12} strokeWidth={2.5} /> : <Check size={12} strokeWidth={3} />}
          {badge}
        </span>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Picture vs. audio · {label}</div>
        <div
          style={{
            marginLeft: "auto",
            fontSize: 11,
            color: "var(--ql-fg-muted)",
            fontFamily: "var(--ql-font-mono)",
          }}
        >
          {report.mismatches}/{report.checked} frames off
        </div>
      </div>

      {drift ? (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 12,
            background: "rgba(248,113,113,.06)",
            border: "1px solid rgba(248,113,113,.22)",
            fontSize: 12,
            color: "var(--ql-fg-muted)",
            lineHeight: 1.55,
          }}
        >
          {blocked ? (
            <>
              <strong style={{ color: "#FCA5A5" }}>The guard stopped this clip before it shipped.</strong>{" "}
              {report.mismatches} of {report.checked} checked frames showed the wrong person for the
              audio, so no video was produced — this is the guard doing its job, not a crash. Make it
              again with a fresh download from{" "}
              <strong style={{ color: "var(--ql-gray-200)" }}>Recover this clip</strong> on the video page.
            </>
          ) : (
            <>
              {report.mismatches} of {report.checked} checked frames show the wrong person for the
              audio — the picture has run off the speech. Make this clip again with a fresh download:
              open it in the Studio and use{" "}
              <strong style={{ color: "var(--ql-gray-200)" }}>Re-cut from scratch</strong> under Edit this clip.
            </>
          )}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", lineHeight: 1.55, marginTop: 12 }}>
          Checked {report.checked} moments across the clip and the on-screen person matched the
          speaker in every one. Note: when both people are on camera at once, a mismatch can be hard
          to spot — worth a quick eyeball on two-person clips.
        </div>
      )}

      {shown.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 14 }}>
          {shown.map((p, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                fontSize: 12,
                color: "var(--ql-fg-muted)",
                lineHeight: 1.5,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--ql-font-mono)",
                  color: p.match ? "#4ADE80" : "#F87171",
                  flexShrink: 0,
                  minWidth: 52,
                }}
              >
                {p.match ? "✓" : "✗"} {p.t.toFixed(1)}s
              </span>
              <span style={{ minWidth: 0 }}>{p.reason}</span>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowAll((v) => !v)}
        style={{
          marginTop: 12,
          background: "none",
          border: "none",
          padding: 0,
          color: "var(--ql-fg-subtle)",
          fontSize: 11,
          fontFamily: "inherit",
          cursor: "pointer",
        }}
      >
        {showAll ? "Show only mismatches" : `Show all ${report.checked} sampled frames`}
      </button>
    </div>
  );
}

export function SyncCard({ jobId, onJobStarted }: {
  jobId: string;
  /** Called after "Apply & re-render" / "Re-translate" started a job, so the
   *  host can follow it (the dialog closes; the page shows progress). */
  onJobStarted?: () => void;
}): React.JSX.Element | null {
  const isMobile = useIsMobile();
  const [report, setReport] = useState<SyncReport | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReport(undefined);
    setLoadError(false);
    fetchSyncReport(jobId)
      .then((r) => { if (!cancelled) setReport(r); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const handleApplyShift = async (): Promise<void> => {
    if (!report?.recommendation) return;
    setBusy(true);
    setError(null);
    try {
      await shiftCaptions(jobId, report.recommendation.offsetMs);
      // Status flips to rendering — the host follows the job.
      onJobStarted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  /** A cue-count mismatch is a STRUCTURAL break in the 1:1 mapping, so the
   *  caption-shift buttons above cannot fix it — they move every cue by the
   *  same amount, while this error grows through the clip. Re-running
   *  translate is the actual remedy, which is what the banner has always
   *  said; this puts the remedy next to the diagnosis instead of making the
   *  operator know they're related and go hunting for it. */
  const handleRetranslate = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await regenerateSubtitles(jobId);
      // Status flips to rendering — the host follows the job.
      onJobStarted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  if (loadError) {
    return <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 20 }}>Couldn't load the caption timing check.</div>;
  }
  if (report === undefined) return null;
  if (report === null) {
    return (
      <div
        style={{
          background: "rgba(16,18,27,.72)",
          border: "1px solid rgba(255,255,255,.06)",
          borderRadius: 18,
          padding: "12px 18px",
          marginBottom: 20,
          fontSize: 12,
          color: "var(--ql-fg-muted)",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <Sparkles size={14} strokeWidth={1.75} color="var(--ql-fg-subtle)" />
        The caption timing check will appear here after the next render.
      </div>
    );
  }

  const verdictColor =
    report.verdict === "green" ? "#4ADE80" : report.verdict === "yellow" ? "#FACC15" : "#F87171";
  const verdictBg =
    report.verdict === "green"
      ? "rgba(74,222,128,.10)"
      : report.verdict === "yellow"
      ? "rgba(250,204,21,.10)"
      : "rgba(248,113,113,.10)";
  const verdictLabel =
    report.verdict === "green"
      ? "Captions and audio are aligned"
      : report.verdict === "yellow"
      ? "Mild drift detected"
      : "Significant drift detected";

  return (
    <div
      style={{
        background: "rgba(16,18,27,.72)",
        border: `1px solid ${verdictColor}33`,
        borderRadius: 18,
        padding: 18,
        marginBottom: 20,
        boxShadow: report.verdict === "red" ? "0 0 30px rgba(248,113,113,.10)" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            color: verdictColor,
            background: verdictBg,
            border: `1px solid ${verdictColor}33`,
            textTransform: "uppercase",
            letterSpacing: ".08em",
          }}
        >
          {report.verdict}
        </span>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Captions · {verdictLabel}</div>
        <div
          style={{
            marginLeft: "auto",
            fontSize: 11,
            color: "var(--ql-fg-muted)",
            fontFamily: "var(--ql-font-mono)",
          }}
        >
          {report.audioDrift.sampleSize}/{report.totalCues} cues sampled
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 10, marginBottom: report.recommendation ? 14 : 0 }}>
        <Metric label="Mean drift" value={`${report.audioDrift.meanMs >= 0 ? "+" : ""}${report.audioDrift.meanMs}ms`} hint="positive = caption late, negative = early" />
        <Metric label="Median" value={`${report.audioDrift.medianMs >= 0 ? "+" : ""}${report.audioDrift.medianMs}ms`} />
        <Metric label="P90 |drift|" value={`${report.audioDrift.p90AbsMs}ms`} />
        <Metric label="σ (stddev)" value={`${report.audioDrift.stddevMs}ms`} hint="low σ = systematic offset" />
      </div>

      {report.recommendation && (
        <div
          style={{
            padding: 14,
            borderRadius: 12,
            background: "rgba(255,213,0,.06)",
            border: "1px solid rgba(255,213,0,.30)",
            display: "flex",
            alignItems: isMobile ? "flex-start" : "center",
            flexDirection: isMobile ? "column" : "row",
            gap: 14,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ql-yellow)", marginBottom: 4 }}>
              Auto-shift recommended: {report.recommendation.offsetMs >= 0 ? "+" : ""}
              {report.recommendation.offsetMs}ms
            </div>
            <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", lineHeight: 1.5 }}>
              {report.recommendation.reason}
            </div>
            {error && (
              <div style={{ marginTop: 6, fontSize: 11, color: "var(--ql-danger)" }}>{error}</div>
            )}
          </div>
          <button
            onClick={handleApplyShift}
            disabled={busy}
            style={{
              padding: "10px 16px",
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 700,
              cursor: busy ? "not-allowed" : "pointer",
              border: "none",
              background: "linear-gradient(135deg,#FFD500,#FFA500)",
              color: "#000",
              fontFamily: "inherit",
              opacity: busy ? 0.5 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {busy ? <Loader2 size={14} className="ql-spin" /> : <Sparkles size={14} strokeWidth={2} />}
            Apply & re-render
          </button>
        </div>
      )}

      {report.parity.countsMatch === false && (
        <div
          style={{
            marginTop: 14,
            padding: 10,
            borderRadius: 10,
            background: "rgba(248,113,113,.06)",
            border: "1px solid rgba(248,113,113,.30)",
            fontSize: 12,
            color: "var(--ql-danger)",
          }}
        >
          <div style={{ marginBottom: 10 }}>
            Cue-count mismatch: {report.parity.arabicCues} caption cues vs {report.parity.englishCues} spoken
            cues. The 1:1 mapping was broken while the captions were written, so they drift further
            out the longer the clip runs — a caption shift cannot fix that, only writing the
            captions again can.
          </div>
          <button
            onClick={() => { void handleRetranslate(); }}
            disabled={busy}
            title="Writes the captions again from the transcript and rebuilds the video. Same action as Regenerate captions under Captions & text."
            style={{
              ...ctaGhostStyle,
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.5 : 1,
            }}
          >
            {busy ? <Loader2 size={14} className="ql-spin" /> : <Languages size={14} strokeWidth={2} />}
            Write the captions again
          </button>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }): React.JSX.Element {
  return (
    <div
      style={{
        padding: "10px 12px",
        borderRadius: 12,
        background: "rgba(255,255,255,.02)",
        border: "1px solid rgba(255,255,255,.06)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "var(--ql-fg-muted)",
          textTransform: "uppercase",
          letterSpacing: ".08em",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          fontFamily: "var(--ql-font-mono)",
          color: "var(--ql-fg)",
          letterSpacing: "-0.01em",
        }}
      >
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 10, color: "var(--ql-fg-subtle)", marginTop: 2, lineHeight: 1.3 }}>{hint}</div>
      )}
    </div>
  );
}
